import React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Combobox } from '@node-space/ui-components'
import { useEarnWalletsOptions } from '../../hooks/useEarnWalletsOptions'

interface EarnSheetWalletSelectProps {
  name: string
  selectedCurrency: string
  disabled?: boolean
  testId?: string
  label?: string
  setMaxAmount?: (value: string) => void
  loadErrorMessage?: string
}

export const EarnSheetWalletSelect = ({
  name,
  selectedCurrency,
  disabled = false,
  testId = 'wallet-select-combobox',
  label,
  setMaxAmount,
  loadErrorMessage,
}: EarnSheetWalletSelectProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  })

  const {
    earnWallets,
    earnWalletsOptions,
    isLoading: isLoadingEarnWallets,
    isError: isEarnWalletsError,
    onSearchChange: setSearchEarnWallets,
  } = useEarnWalletsOptions({
    selectedCurrency,
  })

  const handleSetMaxAmount = (walletId: string) => {
    if (setMaxAmount) {
      setMaxAmount(
        earnWallets?.find(wallet => wallet.id === walletId)?.balance?.value?.toString() || '0'
      )
    }
  }

  const handleOnChange = (value: string) => {
    const selectedWallet = earnWalletsOptions.find(wallet => wallet.value === value) ?? null
    const walletName = typeof selectedWallet?.leftText === 'string' ? selectedWallet.leftText : ''

    field.onChange({
      value: selectedWallet?.value ?? value,
      name: walletName,
    })

    handleSetMaxAmount(value)
  }

  return (
    <Combobox
      {...field}
      onChange={handleOnChange}
      key={`${name}-${selectedCurrency}`}
      id={`wallet-select-${name}`}
      label={label || t('earn.depositSheet.depositFrom')}
      loading={isLoadingEarnWallets}
      selectPlaceholder={t('select')}
      items={earnWalletsOptions}
      errorText={
        error?.message ||
        (isEarnWalletsError
          ? loadErrorMessage || t('earn.depositSheet.walletsLoadError')
          : undefined)
      }
      testId={testId}
      defaultValue={field.value?.value}
      onInputChange={setSearchEarnWallets}
      disabled={disabled || !selectedCurrency}
    />
  )
}
